import { AuthService } from '@/services/auth-service';
import UserService from '@/services/user-service';
import { clearCsrfToken } from '@/services/utils/axios';
import { useAuthStore } from '@/store/use-auth-store';
import { useUserStore } from '@/store/use-user-store';
import { AuthMeResponse } from '@/utils/auth-me';
import {
  hasBackendSessionEverValidated,
  isBackendSessionRecentlyValidated,
  markBackendSessionValidated,
  resetAuthSessionFlags,
} from '@/utils/auth-session';
import { getSessionTenantId } from '@/utils/active-tenant';
import { getErrorMessage } from '@/utils/common';
import {
  applyAuthMeToStores,
  clearAuthQueryCache,
  isBackendUserMismatch,
} from '@/utils/sync-auth-session';
import { clearAuthStoragePreservingPreferences } from '@/utils/storage-utils';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

export type BackendSessionStatus = 'idle' | 'checking' | 'valid' | 'invalid';

/**
 * Validates the LiFT backend session via /auth/me/ and keeps client stores
 * aligned with the cookie session (profile, tenants, permissions cache).
 */
export function useBackendSessionValidation(
  enabled: boolean
): BackendSessionStatus {
  const queryClient = useQueryClient();
  const hasValidatedOnceRef = useRef(hasBackendSessionEverValidated());
  const requestIdRef = useRef(0);
  const [status, setStatus] = useState<BackendSessionStatus>(() =>
    enabled && !hasValidatedOnceRef.current ? 'checking' : 'valid'
  );
  const reset = useAuthStore((state) => state.reset);
  const clearUser = useUserStore((state) => state.clearUser);

  const resolveValidStatus = useCallback(() => {
    hasValidatedOnceRef.current = true;
    setStatus('valid');
  }, []);

  // Validation was disabled (e.g. another tab refreshed the TTL) while this tab
  // was still on the checking spinner — do not stay stuck.
  useEffect(() => {
    if (enabled) {
      return;
    }

    if (isBackendSessionRecentlyValidated()) {
      resolveValidStatus();
    }
  }, [enabled, resolveValidStatus]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const requestId = ++requestIdRef.current;
    const isBackgroundRefresh = hasValidatedOnceRef.current;

    if (!isBackgroundRefresh) {
      setStatus('checking');
    }

    // Send a tenant the user belongs to even when the URL names none of theirs
    // (e.g. a mistyped slug): without one the backend answers 401 "Invalid
    // tenant" for a perfectly valid session.
    UserService.getCurrentUser({
      skipUnauthorizedRedirect: true,
      tenantId: getSessionTenantId() ?? undefined,
    })
      .then((rawData) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        const data = rawData as AuthMeResponse;

        if (isBackendUserMismatch(data)) {
          clearAuthQueryCache(queryClient);
        }

        applyAuthMeToStores(data);
        void queryClient.invalidateQueries({ queryKey: ['userPermissions'] });

        markBackendSessionValidated();
        resolveValidStatus();
      })
      .catch(async (error) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        if (isAxiosError(error) && error.response?.status === 401) {
          // End the server session too, in case the 401 wasn't an expiry.
          await AuthService.logout().catch(() => undefined);
          clearCsrfToken();
          hasValidatedOnceRef.current = false;
          resetAuthSessionFlags();
          reset();
          clearUser();
          clearAuthStoragePreservingPreferences();
          toast.error(
            getErrorMessage(error.response?.data) ??
              'Session expired. Please log in again.'
          );
          setStatus('invalid');
          return;
        }

        markBackendSessionValidated();
        resolveValidStatus();
      });

    return () => {
      requestIdRef.current += 1;
    };
  }, [clearUser, enabled, queryClient, reset, resolveValidStatus]);

  return status;
}
